import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wrench, 
  Users, 
  Calendar, 
  DollarSign, 
  LogOut, 
  User,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Shield,
  TrendingUp,
  Receipt,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Edit,
  Trash2,
  Globe,
  Briefcase,
  Plus,
  Download,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [newPosting, setNewPosting] = useState({ title: '', description: '', location: 'Abbotsford, BC', employment_type: 'Full-time' });
  const [editingPosting, setEditingPosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  
  // Edit dialogs
  const [editMechanicDialog, setEditMechanicDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics(analyticsRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsRange]);

  const fetchAnalytics = async (days) => {
    setAnalyticsLoading(true);
    try {
      const headers = getAuthHeaders();
      const [sumRes, recentRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics/summary?days=${days}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics/recent?limit=50`, { headers, withCredentials: true }),
      ]);
      setAnalytics(sumRes.data);
      setRecentVisits(recentRes.data);
    } catch (error) {
      toast.error('Failed to load analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, applicationsRes, invoicesRes, bookingsRes, usersRes, mechanicsRes, jobPostingsRes, jobAppsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/applications`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/invoices`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/bookings`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/mechanics`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/job-postings`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/job-applications`, { headers, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setApplications(applicationsRes.data);
      setInvoices(invoicesRes.data);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setMechanics(mechanicsRes.data);
      setJobPostings(jobPostingsRes.data);
      setJobApplications(jobAppsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const createJobPosting = async () => {
    if (!newPosting.title.trim() || !newPosting.description.trim()) {
      toast.error('Title and description are required.'); return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/job-postings`, newPosting, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      toast.success('Job posting created.');
      setNewPosting({ title: '', description: '', location: 'Abbotsford, BC', employment_type: 'Full-time' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create posting.');
    }
  };

  const toggleJobPosting = async (postingId, isActive) => {
    try {
      await axios.patch(`${API_URL}/api/admin/job-postings/${postingId}`, { is_active: !isActive }, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      toast.success(isActive ? 'Posting closed.' : 'Posting re-opened.');
      fetchData();
    } catch (error) {
      toast.error('Failed to update posting.');
    }
  };

  const deleteJobPosting = async (postingId) => {
    if (!confirm('Delete this posting permanently? Existing applications stay in the database.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/job-postings/${postingId}`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      toast.success('Posting deleted.');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete posting.');
    }
  };

  const saveEditedPosting = async () => {
    if (!editingPosting?.title?.trim() || !editingPosting?.description?.trim()) {
      toast.error('Title and description are required.'); return;
    }
    try {
      const { posting_id, title, description, location, employment_type } = editingPosting;
      await axios.patch(`${API_URL}/api/admin/job-postings/${posting_id}`,
        { title, description, location, employment_type },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Posting updated.');
      setEditingPosting(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update posting.');
    }
  };

  const downloadResume = async (applicationId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/job-applications/${applicationId}/resume`, {
        headers: getAuthHeaders(), withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `resume_${applicationId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download resume.');
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/job-applications/${applicationId}`, { status }, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      toast.success('Status updated.');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status.');
    }
  };

  const handleApprove = async (applicationId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/applications/${applicationId}/approve`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Application approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (applicationId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/applications/${applicationId}/reject`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Application rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Edit Mechanic
  const openEditMechanic = (mechanic) => {
    setSelectedMechanic({ ...mechanic });
    setEditMechanicDialog(true);
  };

  const handleUpdateMechanic = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/mechanics/${selectedMechanic.mechanic_id}`,
        {
          business_name: selectedMechanic.business_name,
          address: selectedMechanic.address,
          phone: selectedMechanic.phone,
          is_bilingual: selectedMechanic.is_bilingual,
          languages: selectedMechanic.languages || [],
          is_verified: selectedMechanic.is_verified
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Mechanic updated!');
      setEditMechanicDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mechanic');
    }
  };

  // Edit User
  const openEditUser = (u) => {
    setSelectedUser({ ...u });
    setEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${selectedUser.user_id}`,
        {
          name: selectedUser.name,
          role: selectedUser.role
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('User updated!');
      setEditUserDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Delete
  const openDeleteConfirm = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteConfirmDialog(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget.type === 'mechanic') {
        await axios.delete(
          `${API_URL}/api/admin/mechanics/${deleteTarget.item.mechanic_id}`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        toast.success('Mechanic deleted');
      } else if (deleteTarget.type === 'user') {
        await axios.delete(
          `${API_URL}/api/admin/users/${deleteTarget.item.user_id}`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        toast.success('User deleted');
      }
      setDeleteConfirmDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      paid: 'bg-green-100 text-green-800 border-green-200'
    };
    return (
      <Badge className={`${statusClasses[status] || 'bg-gray-100'} border font-medium`}>
        {status?.toUpperCase().replace('_', ' ')}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const pendingApplications = applications.filter(a => a.status === 'pending');
  const processedApplications = applications.filter(a => a.status !== 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/vetting" className="text-slate-300 hover:text-white text-sm hidden md:block">
                Vetting Process
              </Link>
              <div className="flex items-center gap-2 text-white">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <span className="hidden sm:inline text-sm">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-slate-800" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-['Barlow'] text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Manage mechanics, invoices, and monitor platform activity</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-white border border-slate-200" data-testid="stat-users">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                  <p className="text-xs text-slate-600">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-mechanics">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_mechanics || 0}</p>
                  <p className="text-xs text-slate-600">Mechanics</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-bookings">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
                  <p className="text-xs text-slate-600">Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-pending">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_applications || 0}</p>
                  <p className="text-xs text-slate-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-completed">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.completed_bookings || 0}</p>
                  <p className="text-xs text-slate-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-revenue">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">${(stats?.total_revenue || 0).toFixed(0)}</p>
                  <p className="text-xs text-slate-600">Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto p-1 gap-1">
            <TabsTrigger value="pending" className="text-xs sm:text-sm">
              <Clock className="w-4 h-4 mr-1 sm:mr-2" />
              Applications ({pendingApplications.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs sm:text-sm">
              <Receipt className="w-4 h-4 mr-1 sm:mr-2" />
              Invoices ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="text-xs sm:text-sm">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="mechanics" className="text-xs sm:text-sm">
              <Wrench className="w-4 h-4 mr-1 sm:mr-2" />
              Mechanics ({mechanics.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs sm:text-sm" data-testid="tab-jobs">
              <Briefcase className="w-4 h-4 mr-1 sm:mr-2" />
              Jobs ({jobApplications.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="text-xs sm:text-sm" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Pending Applications Tab */}
          <TabsContent value="pending" className="space-y-4">
            {pendingApplications.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No pending applications</h3>
                  <p className="text-slate-600">All applications have been reviewed</p>
                </CardContent>
              </Card>
            ) : (
              pendingApplications.map((app) => (
                <Card key={app.application_id} className="bg-white border border-slate-200" data-testid={`application-${app.application_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-['Barlow'] text-xl font-bold text-slate-900">
                            {app.business_name}
                          </h3>
                          {getStatusBadge(app.status)}
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
                          <div>
                            <p><span className="font-medium">Applicant:</span> {app.name}</p>
                            <p><span className="font-medium">Email:</span> {app.email}</p>
                            <p><span className="font-medium">Phone:</span> {app.phone}</p>
                            <p><span className="font-medium">Address:</span> {app.address}</p>
                          </div>
                          <div>
                            <p><span className="font-medium">License:</span> <span className="font-mono">{app.business_license}</span></p>
                            <p><span className="font-medium">Experience:</span> {app.years_experience} years</p>
                            {app.business_website && (
                              <p>
                                <span className="font-medium">Website:</span>{' '}
                                <a href={app.business_website} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600">
                                  {app.business_website}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-3">
                          <p className="text-sm font-medium text-slate-700 mb-2">Services:</p>
                          <div className="flex flex-wrap gap-2">
                            {app.services_provided.map((service, idx) => (
                              <Badge key={idx} variant="outline" className="bg-slate-50">
                                {service}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 lg:flex-col">
                        <Button 
                          className="bg-green-600 hover:bg-green-700 text-white flex-1"
                          onClick={() => handleApprove(app.application_id)}
                          data-testid={`approve-${app.application_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50 flex-1"
                          onClick={() => handleReject(app.application_id)}
                          data-testid={`reject-${app.application_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {/* Processed Applications */}
            {processedApplications.length > 0 && (
              <div className="mt-8">
                <h3 className="font-['Barlow'] text-lg font-bold text-slate-900 mb-4">Processed Applications</h3>
                <div className="space-y-2">
                  {processedApplications.map((app) => (
                    <Card key={app.application_id} className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h4 className="font-medium text-slate-900">{app.business_name}</h4>
                              {getStatusBadge(app.status)}
                            </div>
                            <p className="text-sm text-slate-600">
                              {app.name} • {app.email} • Reviewed: {formatDate(app.reviewed_at)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
                  <p className="text-slate-600">Invoices will appear here once created</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card key={invoice.invoice_id} className="bg-white border border-slate-200" data-testid={`invoice-${invoice.invoice_id}`}>
                    <CardContent className="p-0">
                      {/* Invoice Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedInvoice(expandedInvoice === invoice.invoice_id ? null : invoice.invoice_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                              <Receipt className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-mono text-sm font-medium text-slate-900">{invoice.invoice_id}</p>
                              <p className="text-sm text-slate-600">
                                {invoice.mechanic_name} → {invoice.customer_name}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-bold text-slate-900">{formatCurrency(invoice.total)}</p>
                              <p className="text-xs text-slate-500">{formatDate(invoice.created_at)}</p>
                            </div>
                            {getStatusBadge(invoice.status)}
                            {expandedInvoice === invoice.invoice_id ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Invoice Details */}
                      {expandedInvoice === invoice.invoice_id && (
                        <div className="border-t border-slate-200 p-4 bg-slate-50">
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Labor Details */}
                            <div>
                              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-blue-500" />
                                Labor Breakdown
                              </h4>
                              <div className="bg-white rounded border border-slate-200 p-3 space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Hours Worked</span>
                                  <span className="font-medium">{invoice.labor_hours} hrs</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Hourly Rate</span>
                                  <span className="font-medium">{formatCurrency(invoice.labor_rate)}/hr</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-100 pt-2">
                                  <span className="text-slate-900 font-medium">Labor Total</span>
                                  <span className="font-bold text-blue-600">{formatCurrency(invoice.labor_total)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Parts Details */}
                            <div>
                              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                                <Wrench className="w-4 h-4 text-green-500" />
                                Parts Breakdown
                              </h4>
                              <div className="bg-white rounded border border-slate-200 p-3 space-y-2 text-sm">
                                {invoice.parts && invoice.parts.length > 0 ? (
                                  <>
                                    {invoice.parts.map((part, idx) => (
                                      <div key={idx} className="flex justify-between">
                                        <span className="text-slate-600">{part.name} (x{part.quantity || 1})</span>
                                        <span className="font-medium">{formatCurrency((part.quantity || 1) * (part.unit_price || 0))}</span>
                                      </div>
                                    ))}
                                    <div className="flex justify-between border-t border-slate-100 pt-2">
                                      <span className="text-slate-900 font-medium">Parts Total</span>
                                      <span className="font-bold text-green-600">{formatCurrency(invoice.parts_total)}</span>
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-slate-500 italic">No parts listed</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Receipts */}
                          {invoice.receipts && invoice.receipts.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-purple-500" />
                                Attached Receipts
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {invoice.receipts.map((receipt, idx) => (
                                  <a 
                                    key={idx}
                                    href={receipt.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-white border border-slate-200 rounded px-3 py-2 text-sm hover:border-orange-300 transition-colors"
                                  >
                                    <FileText className="w-4 h-4 text-slate-400" />
                                    <span>{receipt.filename || receipt.description || `Receipt ${idx + 1}`}</span>
                                    <ExternalLink className="w-3 h-3 text-slate-400" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Invoice Total */}
                          <div className="mt-4 bg-slate-900 rounded p-4">
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-slate-400 text-xs uppercase">Subtotal</p>
                                <p className="text-white font-bold">{formatCurrency(invoice.subtotal)}</p>
                              </div>
                              <div>
                                <p className="text-slate-400 text-xs uppercase">Tax (8%)</p>
                                <p className="text-white font-bold">{formatCurrency(invoice.tax)}</p>
                              </div>
                              <div>
                                <p className="text-orange-400 text-xs uppercase">Total</p>
                                <p className="text-orange-500 text-xl font-bold">{formatCurrency(invoice.total)}</p>
                              </div>
                            </div>
                          </div>

                          {/* Notes */}
                          {invoice.notes && (
                            <div className="mt-4 bg-white rounded border border-slate-200 p-3">
                              <p className="text-sm text-slate-600"><span className="font-medium">Notes:</span> {invoice.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
                  <p className="text-slate-600">Bookings will appear here once customers book services</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full bg-white border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Booking ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Mechanic</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bookings.map((booking) => (
                      <tr key={booking.booking_id} className="hover:bg-slate-50" data-testid={`booking-${booking.booking_id}`}>
                        <td className="px-4 py-3 font-mono text-sm text-slate-900">{booking.booking_id}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{booking.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{booking.mechanic_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{booking.service_type}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{booking.preferred_date} {booking.preferred_time}</td>
                        <td className="px-4 py-3">{getStatusBadge(booking.status)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{booking.total_amount ? formatCurrency(booking.total_amount) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {users.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No users yet</h3>
                  <p className="text-slate-600">Users will appear here once they register</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full bg-white border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="text-sm font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={
                            u.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                            u.role === 'mechanic' ? 'bg-green-50 text-green-700 border-green-200' :
                            'bg-blue-50 text-blue-700 border-blue-200'
                          }>
                            {u.role?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.user_id}`}>
                              <Edit className="w-4 h-4 text-slate-500" />
                            </Button>
                            {u.role !== 'admin' && (
                              <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm('user', u)} data-testid={`delete-user-${u.user_id}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Mechanics Tab */}
          <TabsContent value="mechanics" className="space-y-4">
            {mechanics.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No mechanics yet</h3>
                  <p className="text-slate-600">Approved mechanics will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {mechanics.map((mechanic) => (
                  <Card key={mechanic.mechanic_id} className="bg-white border border-slate-200" data-testid={`mechanic-${mechanic.mechanic_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-['Barlow'] font-bold text-slate-900 truncate">{mechanic.business_name}</h4>
                            {mechanic.is_verified && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                            {mechanic.is_bilingual && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                                <Globe className="w-3 h-3 mr-1" />
                                Bilingual
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 truncate">{mechanic.email}</p>
                          {mechanic.is_bilingual && mechanic.languages?.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">{mechanic.languages.join(', ')}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-slate-600">
                              <span className="font-medium text-slate-900">{mechanic.rating || 0}</span> ★
                            </span>
                            <span className="text-slate-600">
                              <span className="font-medium text-slate-900">{mechanic.total_reviews || 0}</span> reviews
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(mechanic.services || []).slice(0, 3).map((service, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-slate-50">
                                {service}
                              </Badge>
                            ))}
                            {(mechanic.services || []).length > 3 && (
                              <Badge variant="outline" className="text-xs bg-slate-50">
                                +{mechanic.services.length - 3} more
                              </Badge>
                            )}
                          </div>
                          {/* Action Buttons */}
                          <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                            <Button variant="outline" size="sm" onClick={() => openEditMechanic(mechanic)} data-testid={`edit-mechanic-${mechanic.mechanic_id}`}>
                              <Edit className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openDeleteConfirm('mechanic', mechanic)} data-testid={`delete-mechanic-${mechanic.mechanic_id}`}>
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jobs / Careers Tab */}
          <TabsContent value="jobs" className="space-y-6" data-testid="jobs-tab-content">
            {/* Create posting */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Create New Job Posting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="np-title">Title</Label>
                    <Input
                      id="np-title"
                      placeholder="e.g. Field Representative"
                      value={newPosting.title}
                      onChange={e => setNewPosting({ ...newPosting, title: e.target.value })}
                      data-testid="new-posting-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="np-location">Location</Label>
                    <Input
                      id="np-location"
                      value={newPosting.location}
                      onChange={e => setNewPosting({ ...newPosting, location: e.target.value })}
                      data-testid="new-posting-location"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="np-type">Employment Type</Label>
                  <select
                    id="np-type"
                    className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={newPosting.employment_type}
                    onChange={e => setNewPosting({ ...newPosting, employment_type: e.target.value })}
                    data-testid="new-posting-type"
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="np-desc">Description</Label>
                  <textarea
                    id="np-desc"
                    rows={4}
                    className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    placeholder="What the role involves, who we're looking for, what success looks like..."
                    value={newPosting.description}
                    onChange={e => setNewPosting({ ...newPosting, description: e.target.value })}
                    data-testid="new-posting-description"
                  />
                </div>
                <Button onClick={createJobPosting} className="bg-orange-500 hover:bg-orange-600" data-testid="create-posting-btn">
                  Publish Posting
                </Button>
              </CardContent>
            </Card>

            {/* Active postings list */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Current Postings ({jobPostings.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {jobPostings.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No postings yet — create your first one above.</p>
                ) : (
                  <div className="space-y-3">
                    {jobPostings.map(p => (
                      <div
                        key={p.posting_id}
                        className="flex items-start justify-between border border-slate-200 rounded-md p-3 gap-3"
                        data-testid={`admin-posting-${p.posting_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-slate-900">{p.title}</p>
                            <Badge className={p.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-200 text-slate-700'}>
                              {p.is_active ? 'Active' : 'Closed'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{p.location} · {p.employment_type}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingPosting({ ...p })}
                            data-testid={`edit-posting-${p.posting_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleJobPosting(p.posting_id, p.is_active)}
                            data-testid={`toggle-posting-${p.posting_id}`}
                          >
                            {p.is_active ? 'Close' : 'Reopen'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteJobPosting(p.posting_id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-posting-${p.posting_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applications */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Job Applications ({jobApplications.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {jobApplications.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No applications yet.</p>
                ) : (
                  <div className="space-y-3">
                    {jobApplications.map(a => (
                      <div
                        key={a.application_id}
                        className="border border-slate-200 rounded-md p-3"
                        data-testid={`job-app-${a.application_id}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-[240px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-slate-900">{a.name}</p>
                              <Badge className="text-xs">{a.position_title}</Badge>
                              <Badge variant="outline" className="text-xs">{a.status}</Badge>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              <a href={`mailto:${a.email}`} className="text-orange-600 hover:underline">{a.email}</a>
                              {' · '}
                              <a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a>
                            </p>
                            <p className="text-xs text-slate-500 mt-1">Submitted: {new Date(a.submitted_at).toLocaleString()}</p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadResume(a.application_id, a.resume_filename)}
                              data-testid={`download-resume-${a.application_id}`}
                            >
                              <Download className="w-4 h-4 mr-1" /> Resume
                            </Button>
                            <select
                              className="text-xs border border-slate-300 rounded px-2"
                              value={a.status}
                              onChange={e => updateApplicationStatus(a.application_id, e.target.value)}
                              data-testid={`status-select-${a.application_id}`}
                            >
                              <option value="new">New</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="contacted">Contacted</option>
                              <option value="rejected">Rejected</option>
                              <option value="hired">Hired</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4" data-testid="analytics-tab-content">
            <Card className="bg-white border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <BarChart3 className="w-5 h-5 text-orange-500" />
                  Visitor Analytics
                </CardTitle>
                <div className="flex items-center gap-2">
                  {[7, 30, 90].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant={analyticsRange === d ? 'default' : 'outline'}
                      onClick={() => setAnalyticsRange(d)}
                      className={analyticsRange === d ? 'bg-orange-500 hover:bg-orange-600' : ''}
                      data-testid={`analytics-range-${d}`}
                    >
                      {d}d
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {analyticsLoading && !analytics ? (
                  <div className="py-12 text-center text-slate-500">Loading analytics…</div>
                ) : !analytics ? (
                  <div className="py-12 text-center text-slate-500">No analytics data yet.</div>
                ) : (
                  <div className="space-y-6">
                    {/* Top stat cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="stat-total-visits">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total visits</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.totals.visits.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">last {analytics.range_days} days</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="stat-unique-visitors">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Unique visitors</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.totals.unique_visitors.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">last {analytics.range_days} days</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="stat-today">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Today</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.totals.today.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">visits so far</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="stat-yesterday">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Yesterday</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.totals.yesterday.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">visits</p>
                      </div>
                    </div>

                    {/* Daily traffic mini chart */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 text-sm">Daily traffic</h4>
                      {analytics.daily.length === 0 ? (
                        <p className="text-sm text-slate-500">No traffic recorded yet.</p>
                      ) : (
                        <div className="flex items-end gap-1 h-32 border-b border-l border-slate-200 p-2" data-testid="daily-chart">
                          {(() => {
                            const max = Math.max(...analytics.daily.map(d => d.visits), 1);
                            return analytics.daily.map((d) => (
                              <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                                <div
                                  className="w-full bg-orange-500 hover:bg-orange-600 rounded-t transition-all"
                                  style={{ height: `${Math.max((d.visits / max) * 100, 4)}%` }}
                                  title={`${d.date}: ${d.visits} visits, ${d.visitors} visitors`}
                                ></div>
                                <span className="hidden group-hover:block absolute -top-8 bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                  {d.date}: {d.visits}
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-2">{analytics.daily.length} days with traffic. Hover bars for date.</p>
                    </div>

                    {/* Two-column: Top pages + Top referrers */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 text-sm">Top pages</h4>
                        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="top-pages">
                          {analytics.top_pages.length === 0 ? (
                            <p className="p-4 text-sm text-slate-500">No data.</p>
                          ) : analytics.top_pages.map((p) => (
                            <div key={p.path} className="flex items-center justify-between px-3 py-2 text-sm">
                              <span className="text-slate-700 truncate mr-2" title={p.path}>{p.path}</span>
                              <span className="font-semibold text-slate-900">{p.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3 text-sm">Top referrers</h4>
                        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="top-referrers">
                          {analytics.top_referrers.length === 0 ? (
                            <p className="p-4 text-sm text-slate-500">No external referrers yet — most visits are direct.</p>
                          ) : analytics.top_referrers.map((r) => (
                            <div key={r.referrer} className="flex items-center justify-between px-3 py-2 text-sm">
                              <span className="text-slate-700 truncate mr-2" title={r.referrer}>{r.referrer}</span>
                              <span className="font-semibold text-slate-900">{r.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Devices */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 text-sm">Devices</h4>
                      <div className="flex flex-wrap gap-2" data-testid="device-breakdown">
                        {analytics.devices.length === 0 ? (
                          <p className="text-sm text-slate-500">No data.</p>
                        ) : analytics.devices.map((d) => (
                          <Badge key={d.device} variant="outline" className="text-sm">
                            {d.device}: <span className="ml-1 font-semibold">{d.count}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Recent visits */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 text-sm">Recent activity (last 50)</h4>
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="recent-visits">
                        <div className="max-h-80 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr className="text-left text-slate-500">
                                <th className="px-3 py-2 font-medium">Time</th>
                                <th className="px-3 py-2 font-medium">Path</th>
                                <th className="px-3 py-2 font-medium">Device</th>
                                <th className="px-3 py-2 font-medium">Visitor</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {recentVisits.length === 0 ? (
                                <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">No visits yet.</td></tr>
                              ) : recentVisits.map((v) => (
                                <tr key={v.visit_id} className="hover:bg-slate-50">
                                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{new Date(v.timestamp).toLocaleString()}</td>
                                  <td className="px-3 py-2 text-slate-800 truncate max-w-[200px]" title={v.path}>{v.path}</td>
                                  <td className="px-3 py-2 text-slate-600">{v.device}</td>
                                  <td className="px-3 py-2 text-slate-400 font-mono text-xs">{(v.visitor_id || '').slice(0, 8)}{v.user_id ? ' (auth)' : ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit job posting modal */}
      {editingPosting && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
          onClick={() => setEditingPosting(null)}
          data-testid="edit-posting-modal"
        >
          <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-xl text-slate-900">Edit Job Posting</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ep-title">Title</Label>
                  <Input
                    id="ep-title"
                    value={editingPosting.title}
                    onChange={e => setEditingPosting({ ...editingPosting, title: e.target.value })}
                    data-testid="edit-posting-title"
                  />
                </div>
                <div>
                  <Label htmlFor="ep-location">Location</Label>
                  <Input
                    id="ep-location"
                    value={editingPosting.location || ''}
                    onChange={e => setEditingPosting({ ...editingPosting, location: e.target.value })}
                    data-testid="edit-posting-location"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ep-type">Employment Type</Label>
                <select
                  id="ep-type"
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={editingPosting.employment_type || 'Full-time'}
                  onChange={e => setEditingPosting({ ...editingPosting, employment_type: e.target.value })}
                  data-testid="edit-posting-type"
                >
                  <option>Full-time</option>
                  <option>Part-time</option>
                  <option>Contract</option>
                  <option>Internship</option>
                </select>
              </div>
              <div>
                <Label htmlFor="ep-desc">Description</Label>
                <textarea
                  id="ep-desc"
                  rows={8}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={editingPosting.description}
                  onChange={e => setEditingPosting({ ...editingPosting, description: e.target.value })}
                  data-testid="edit-posting-description"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingPosting(null)}
                  className="flex-1"
                  data-testid="cancel-edit-posting"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveEditedPosting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  data-testid="save-edit-posting"
                >
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Mechanic Dialog */}
      <Dialog open={editMechanicDialog} onOpenChange={setEditMechanicDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Mechanic</DialogTitle>
          </DialogHeader>
          {selectedMechanic && (
            <div className="space-y-4">
              <div>
                <Label>Business Name</Label>
                <Input
                  value={selectedMechanic.business_name || ''}
                  onChange={(e) => setSelectedMechanic({...selectedMechanic, business_name: e.target.value})}
                  data-testid="edit-mechanic-name"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={selectedMechanic.address || ''}
                  onChange={(e) => setSelectedMechanic({...selectedMechanic, address: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={selectedMechanic.phone || ''}
                  onChange={(e) => setSelectedMechanic({...selectedMechanic, phone: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedMechanic.is_bilingual || false}
                  onCheckedChange={(checked) => setSelectedMechanic({...selectedMechanic, is_bilingual: checked})}
                  id="edit-bilingual"
                />
                <Label htmlFor="edit-bilingual">Bilingual</Label>
              </div>
              {selectedMechanic.is_bilingual && (
                <div>
                  <Label>Languages (comma-separated)</Label>
                  <Input
                    value={(selectedMechanic.languages || []).join(', ')}
                    onChange={(e) => setSelectedMechanic({...selectedMechanic, languages: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                    placeholder="English, Mandarin, Punjabi, French, Spanish..."
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedMechanic.is_verified || false}
                  onCheckedChange={(checked) => setSelectedMechanic({...selectedMechanic, is_verified: checked})}
                  id="edit-verified"
                />
                <Label htmlFor="edit-verified">Verified</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMechanicDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateMechanic} data-testid="save-mechanic-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={selectedUser.name || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label>Email (read-only)</Label>
                <Input value={selectedUser.email || ''} disabled className="bg-slate-100" />
              </div>
              <div>
                <Label>Role</Label>
                <select
                  className="w-full border border-slate-200 rounded-md px-3 py-2"
                  value={selectedUser.role || 'customer'}
                  onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                  data-testid="edit-user-role"
                >
                  <option value="customer">Customer</option>
                  <option value="mechanic">Mechanic</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} data-testid="save-user-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete this {deleteTarget?.type}? This action cannot be undone.
          </p>
          {deleteTarget && (
            <p className="font-medium text-slate-900">
              {deleteTarget.type === 'mechanic' ? deleteTarget.item.business_name : deleteTarget.item.name}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
